// src/pages/ProductDetailPage.jsx
import React, { useState, useEffect, useRef } from 'react';
import {
  Minus, Plus, CheckCircle, Truck, MapPin, BadgeCheck,
  MessageCircle, Star, Send, X, Sparkles, Loader2, 
  TrendingUp, Package, Heart
} from 'lucide-react';
import { getUnitPriceFromTiers } from '../utils/moqUtils';
import { useApp } from '../context/AppContext';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  doc, getDoc, collection, query, where, onSnapshot,
  addDoc, serverTimestamp, updateDoc
} from 'firebase/firestore';
import { db } from '../utils/firebase';
import { getChatId } from '../utils/chatUtils';
import ProductCard from '../components/ProductCard';

const ProductDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation(); // ✅ URL state 확인용
  const { addToCart, user, getProductRecommendationsAI } = useApp();

  const [product, setProduct] = useState(null);
  const [sellerProfile, setSellerProfile] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  // 채팅 모달 상태
  const [showChatModal, setShowChatModal] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loadingChat, setLoadingChat] = useState(false);
  const messagesEndRef = useRef(null);

  // AI 추천 상태
  const [recommendedProducts, setRecommendedProducts] = useState([]);
  const [recommendationStrategy, setRecommendationStrategy] = useState('');
  const [loadingRecommendations, setLoadingRecommendations] = useState(false);

  // ✅ URL state로 채팅 자동 열기
  useEffect(() => {
    if (location.state?.openChat) {
      setShowChatModal(true);
    }
  }, [location.state]);

  // 제품 및 판매자 로드
  useEffect(() => {
    if (!id) {
      setError('Invalid product ID');
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      try {
        console.log('=== ProductDetailPage Debug ===');
        console.log('Fetching product with ID:', id);
        console.log('User authentication status:', user ? 'authenticated' : 'not authenticated');
        console.log('User type:', user?.userType || 'none');
        
        const productDoc = await getDoc(doc(db, 'products', id));
        console.log('Product document exists:', productDoc.exists());
        
        if (!productDoc.exists()) {
          console.log('Product document does not exist');
          setError('Product not found');
          return;
        }

        const productData = productDoc.data();
        console.log('Product data:', productData);
        console.log('Product status:', productData.status);
        
        if (productData.status !== 'approved') {
          console.log('Product is not approved, status:', productData.status);
          setError(`Product not approved (status: ${productData.status})`);
          return;
        }

        const fullProductData = { id: productDoc.id, ...productData };
        console.log('Full product data:', fullProductData);
        
        // moqPrices 배열이 존재하는지 확인
        if (!fullProductData.moqPrices || !Array.isArray(fullProductData.moqPrices)) {
          console.error('Invalid moqPrices structure:', fullProductData.moqPrices);
          setError('Invalid product data structure - moqPrices missing or invalid');
          return;
        }
        
        console.log('MOQ Prices:', fullProductData.moqPrices);
        const minQty = fullProductData.moqPrices[0]?.minQty || 1;
        setQuantity(minQty);
        setProduct(fullProductData);
        console.log('Product loaded successfully');

        if (fullProductData.sellerId) {
          try {
            console.log('Fetching seller profile for:', fullProductData.sellerId);
            const sellerDoc = await getDoc(doc(db, 'users', fullProductData.sellerId));
            if (sellerDoc.exists()) {
              setSellerProfile(sellerDoc.data());
              console.log('Seller profile loaded');
            } else {
              console.log('Seller profile not found');
            }
          } catch (sellerErr) {
            console.warn('Failed to fetch seller profile:', sellerErr);
            // 판매자 프로필 로드 실패는 상품 로드 실패로 이어지지 않음
          }
        }
      } catch (err) {
        console.error('=== Error fetching product ===');
        console.error('Error:', err);
        console.error('Error code:', err.code);
        console.error('Error message:', err.message);
        console.error('Error stack:', err.stack);
        setError(`Failed to load product: ${err.message} (Code: ${err.code})`);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, user]);

  // 댓글 로드
  useEffect(() => {
    if (!id) return;
    const q = query(collection(db, 'comments'), where('productId', '==', id));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const commentsList = snapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .sort((a, b) => b.timestamp?.seconds - a.timestamp?.seconds);
      setComments(commentsList);
    });
    return () => unsubscribe();
  }, [id]);

  // AI 추천 로드
  useEffect(() => {
    if (!product || !getProductRecommendationsAI) return;

    const loadRecommendations = async () => {
      setLoadingRecommendations(true);
      try {
        const result = await getProductRecommendationsAI(product);
        setRecommendedProducts(result.products || []);
        setRecommendationStrategy(result.strategy || '');
      } catch (error) {
        console.error('Failed to load recommendations:', error);
        setRecommendedProducts([]);
        setRecommendationStrategy('');
      } finally {
        setLoadingRecommendations(false);
      }
    };

    loadRecommendations();
  }, [product, getProductRecommendationsAI]);

  // 채팅 로드
  useEffect(() => {
    if (!showChatModal || !user?.uid || !product?.sellerId) return;

    const chatId = getChatId(user.uid, product.sellerId);
    setLoadingChat(true);

    const messagesRef = collection(db, 'messages', chatId, 'messages');
    const q = query(messagesRef);
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const msgs = snapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .sort((a, b) => a.timestamp?.seconds - b.timestamp?.seconds);
      setChatMessages(msgs);
      setLoadingChat(false);
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    });

    // 채팅방 생성
    const chatDocRef = doc(db, 'messages', chatId);
    getDoc(chatDocRef).then(docSnap => {
      if (!docSnap.exists()) {
        updateDoc(chatDocRef, {
          buyerId: user.uid,
          sellerId: product.sellerId,
          createdAt: serverTimestamp(),
          lastMessage: '',
          unreadCount: 0
        });
      }
    });

    return () => unsubscribe();
  }, [showChatModal, user, product]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto py-12 text-center">
        <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent"></div>
        <p className="mt-4 text-gray-600">Loading product details...</p>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="max-w-4xl mx-auto py-12 text-center">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-4">
          <h2 className="text-lg font-semibold text-red-800 mb-2">Failed to load product</h2>
          <p className="text-red-600 mb-2">{error || 'Product not found.'}</p>
          <p className="text-sm text-red-500">
            Product ID: {id}
          </p>
        </div>
        <button onClick={() => navigate('/')} className="mt-4 text-blue-600 hover:text-blue-800 font-medium">
          ← Back to Home
        </button>
      </div>
    );
  }

  const currentUnitPrice = getUnitPriceFromTiers(product.moqPrices, quantity);
  const currentTier = product.moqPrices.find(tier =>
    quantity >= tier.minQty && quantity <= tier.maxQty
  );
  const minQty = product.moqPrices[0]?.minQty || 1;
  const total = (currentUnitPrice * quantity).toFixed(2);

  // 스펙 (기존 유지)
  const specs = [
    { label: 'Material', value: product.material || 'N/A' },
    { label: 'Color', value: product.color || 'N/A' },
    { label: 'Dimensions', value: product.dimensions ? `${product.dimensions} mm` : 'N/A' },
    { label: 'Weight', value: product.weight ? `${product.weight} kg` : 'N/A' },
    { label: 'Packaging', value: product.packagingUnit ? `${product.packagingUnit} units/box` : 'N/A' },
    { label: 'Origin', value: product.origin || 'N/A' },
    { label: 'Certifications', value: product.certifications || 'None' },
    { label: 'SKU', value: product.sku || 'N/A' }, // ✅ SKU 추가
  ].filter(item => item.value !== 'N/A');

  // 별점 평균 계산
  const avgRating = comments.length > 0
    ? (comments.reduce((sum, c) => sum + c.rating, 0) / comments.length).toFixed(1)
    : 0;

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !user?.uid || !product?.sellerId) return;
    const chatId = getChatId(user.uid, product.sellerId);
    try {
      await addDoc(collection(db, 'messages', chatId, 'messages'), {
        senderId: user.uid,
        text: newMessage.trim(),
        timestamp: serverTimestamp()
      });
      setNewMessage('');
    } catch (err) {
      alert('Failed to send message');
      console.error(err);
    }
  };

  const openChat = () => {
    if (!user) {
      alert('Please sign in to contact the seller.');
      return;
    }
    setShowChatModal(true);
  };

  const renderStars = (rating) => (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          size={16}
          fill={star <= rating ? "currentColor" : "none"}
          className="text-yellow-500"
        />
      ))}
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto py-4 sm:py-6 px-4 sm:px-6 lg:px-8">
      <button 
        onClick={() => navigate(-1)}
        className="mb-4 sm:mb-6 text-gray-900 hover:text-gray-700 flex items-center gap-2 font-medium text-sm sm:text-base"
      >
        ← Back
      </button>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
        {/* 이미지 */}
        <div className="space-y-3 sm:space-y-4">
          <div className="aspect-square w-full bg-gray-50 rounded-xl sm:rounded-2xl overflow-hidden shadow-sm border border-gray-200">
            <img 
              src={product.images[selectedImageIndex]} 
              alt={product.name}
              className="w-full h-full object-contain p-2 sm:p-4"
            />
          </div>
          {product.images.length > 1 && (
            <div className="flex gap-2 sm:gap-3 overflow-x-auto pb-2 hide-scrollbar">
              {product.images.map((img, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedImageIndex(index)}
                  className={`flex-shrink-0 w-12 h-12 sm:w-16 sm:h-16 rounded-lg overflow-hidden border-2 ${
                    selectedImageIndex === index ? 'border-blue-600' : 'border-gray-300'
                  }`}
                >
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* 정보 */}
        <div className="space-y-4 sm:space-y-6">
          <div>
            <span className="inline-block text-xs font-semibold text-blue-700 bg-blue-100 px-2 py-1 rounded">
              {product.category || 'General'}
            </span>
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mt-2 mb-1 leading-tight">
              {product.name}
            </h1>
            {/* ✅ SKU & 별점 */}
            <div className="flex items-center gap-2 sm:gap-3 text-xs sm:text-sm text-gray-600 mb-2">
              <span className="hidden sm:inline">SKU: {product.sku || 'N/A'}</span>
              {avgRating > 0 && (
                <>
                  <span className="hidden sm:inline">•</span>
                  <div className="flex items-center gap-1">
                    {renderStars(avgRating)}
                    <span className="text-xs sm:text-sm">({comments.length} reviews)</span>
                  </div>
                </>
              )}
              <span className="text-xs sm:text-sm">• {product.orderCount || 0} orders</span>
            </div>
            <p className="text-gray-600 text-sm sm:text-base mb-3">{product.description}</p>
            
            <div className="mt-4 p-3 sm:p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <BadgeCheck className="text-green-600" size={16} />
                    <span className="font-semibold text-gray-900 text-sm sm:text-base">Sold by: {product.sellerName}</span>
                  </div>
                  {sellerProfile?.company && (
                    <p className="text-xs sm:text-sm text-gray-700 mt-1">
                      <span className="font-medium">Company:</span> {sellerProfile.company}
                    </p>
                  )}
                </div>
                <button
                  onClick={openChat}
                  className="flex items-center gap-1 text-blue-600 hover:text-blue-800 font-medium text-xs sm:text-sm ml-2"
                >
                  <MessageCircle size={14} />
                  <span className="hidden sm:inline">Message Seller</span>
                  <span className="sm:hidden">Chat</span>
                </button>
              </div>
            </div>
          </div>

          {/* MOQ, 스펙, 수량, 가격, CTA 등 — 기존 코드 유지 */}

          {/* MOQ Pricing */}
          <div className="bg-gray-50 rounded-xl p-3 sm:p-4">
            <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2 text-sm sm:text-base">
              <Truck size={16} /> <span className="hidden sm:inline">MOQ Pricing (Minimum Order Quantity)</span><span className="sm:hidden">MOQ Pricing</span>
            </h3>
            <div className="space-y-2">
              {product.moqPrices.map((tier, index) => (
                <div key={index} className="flex justify-between items-center py-1 sm:py-2">
                  <span className="text-xs sm:text-sm font-medium">
                    {tier.minQty}–{tier.maxQty === 999999 ? '∞' : tier.maxQty} units
                  </span>
                  <span className="text-sm sm:text-lg font-bold text-blue-700">${tier.unitPrice.toFixed(2)}</span>
                </div>
              ))}
            </div>
          </div>

          {/* 스펙 */}
          {specs.length > 0 && (
            <div className="border border-gray-200 rounded-xl p-3 sm:p-4">
              <h3 className="font-semibold text-gray-900 mb-3 text-sm sm:text-base">Product Specifications</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs sm:text-sm">
                {specs.map((spec, i) => (
                  <div key={i} className="flex">
                    <span className="text-gray-500 w-20 sm:w-24 flex-shrink-0">{spec.label}:</span>
                    <span className="font-medium">{spec.value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 수량 선택 */}
          <div className="border border-gray-200 rounded-xl p-3 sm:p-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Quantity (MOQ: {minQty}+)
            </label>
            <div className="flex items-center gap-2 sm:gap-3">
              <button
                onClick={() => setQuantity(Math.max(minQty, quantity - 1))}
                disabled={quantity <= minQty}
                className="bg-gray-200 rounded-l-full w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center hover:bg-gray-300 disabled:opacity-50"
              >
                <Minus size={14} />
              </button>
              <input
                type="number"
                min={minQty}
                value={quantity}
                onChange={(e) => {
                  const val = parseInt(e.target.value);
                  if (!isNaN(val) && val >= minQty) setQuantity(val);
                }}
                className="w-16 sm:w-20 px-2 sm:px-3 py-2 border-y border-gray-300 text-center focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm"
              />
              <button
                onClick={() => setQuantity(quantity + 1)}
                className="bg-gray-200 rounded-r-full w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center hover:bg-gray-300"
              >
                <Plus size={14} />
              </button>
              <span className="text-gray-600 ml-1 sm:ml-2 text-sm">units</span>
            </div>
          </div>

          {/* 가격 요약 */}
          <div className="bg-blue-50 rounded-xl p-3 sm:p-4 border border-blue-100">
            <div className="flex justify-between items-center mb-2">
              <span className="text-base sm:text-lg font-bold text-gray-900">Total: ${total}</span>
              <span className="text-xs sm:text-sm text-gray-600">(${currentUnitPrice.toFixed(2)}/unit)</span>
            </div>
            {currentTier && currentTier.minQty > minQty && (
              <div className="flex items-center gap-2 text-green-700 mt-2">
                <CheckCircle size={14} />
                <span className="text-xs sm:text-sm font-medium">Wholesale discount applied!</span>
              </div>
            )}
          </div>

          {/* CTA 버튼 */}
          <button
            onClick={() => addToCart(product, quantity)}
            disabled={quantity < minQty || !user}
            className={`w-full py-2.5 sm:py-3 px-4 rounded-xl font-semibold text-white transition-colors text-sm sm:text-base ${
              user 
                ? 'bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400' 
                : 'bg-gray-500 cursor-not-allowed'
            }`}
          >
            {user 
              ? `Add to Cart — $${total}`
              : 'Sign In to Add to Cart'
            }
          </button>

          {/* 배송 정보 */}
          <div className="text-xs sm:text-sm text-gray-600 p-3 bg-gray-50 rounded-xl">
            <div className="flex items-center gap-2 mb-1">
              <Truck size={14} />
              <span className="font-medium">Shipping</span>
            </div>
            <p>Ships within 2–3 business days.</p>
            <p className="mt-1 flex items-center gap-1">
              <MapPin size={12} /> Ships from: {product.shippingFrom || 'Seoul, South Korea'}
            </p>
          </div>
        </div>
      </div>

      {/* AI 추천 상품 섹션 */}
      {recommendedProducts.length > 0 && (
        <div className="mt-8 sm:mt-16">
          <div className="flex items-center gap-3 mb-6">
            <div className="flex items-center gap-2">
              <Sparkles className="text-blue-600" size={20} />
              <h2 className="text-lg sm:text-xl font-bold text-gray-900">
                AI Recommended Products
              </h2>
            </div>
            {recommendationStrategy && (
              <div className="hidden sm:flex items-center gap-2 text-sm text-gray-600 bg-blue-50 px-3 py-1 rounded-full">
                <TrendingUp size={14} />
                <span>{recommendationStrategy}</span>
              </div>
            )}
          </div>

          {loadingRecommendations ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="animate-spin text-blue-600" size={24} />
              <span className="ml-2 text-gray-600">Loading AI recommendations...</span>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4 sm:gap-6">
              {recommendedProducts.slice(0, 6).map((recProduct) => (
                <div key={recProduct.id} className="relative group">
                  <div className="bg-white rounded-lg border border-gray-200 p-3 sm:p-4 hover:shadow-lg transition-shadow">
                    <div className="aspect-square mb-3 bg-gray-50 rounded-lg overflow-hidden">
                      <img
                        src={recProduct.images?.[0] || '/placeholder-product.jpg'}
                        alt={recProduct.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                      />
                    </div>
                    <h3 className="font-medium text-sm text-gray-900 mb-1 line-clamp-2">
                      {recProduct.name}
                    </h3>
                    <p className="text-xs text-gray-600 mb-2 line-clamp-1">
                      {recProduct.category}
                    </p>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-bold text-blue-600">
                        ${recProduct.moqPrices?.[0]?.price || 0}
                      </span>
                      {recProduct.relevanceScore && (
                        <span className="text-xs text-green-600 font-medium">
                          {Math.round(recProduct.relevanceScore * 100)}% match
                        </span>
                      )}
                    </div>
                    {recProduct.recommendationReason && (
                      <p className="text-xs text-blue-600 mb-2 line-clamp-2">
                        {recProduct.recommendationReason}
                      </p>
                    )}
                    <button
                      onClick={() => navigate(`/product/${recProduct.id}`)}
                      className="w-full text-xs bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      View Product
                    </button>
                  </div>
                  
                  {/* 추천 타입 배지 */}
                  {recProduct.recommendationType && (
                    <div className="absolute top-2 right-2">
                      {recProduct.recommendationType === 'similar' && (
                        <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full flex items-center gap-1">
                          <Package size={10} />
                          Similar
                        </span>
                      )}
                      {recProduct.recommendationType === 'complementary' && (
                        <span className="bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded-full flex items-center gap-1">
                          <Heart size={10} />
                          Goes Well
                        </span>
                      )}
                      {recProduct.recommendationType === 'trending' && (
                        <span className="bg-orange-100 text-orange-800 text-xs px-2 py-1 rounded-full flex items-center gap-1">
                          <TrendingUp size={10} />
                          Trending
                        </span>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {recommendationStrategy && (
            <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
              <div className="flex items-start gap-2">
                <Sparkles className="text-blue-600 mt-0.5" size={16} />
                <div>
                  <p className="text-sm font-medium text-blue-900">AI Recommendation Strategy</p>
                  <p className="text-xs text-blue-700 mt-1">{recommendationStrategy}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* 댓글 섹션 — 기존 유지 */}
      <div className="mt-8 sm:mt-16">
        <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-4">
          Customer Reviews ({comments.length})
        </h2>
        {comments.length > 0 ? (
          <div className="space-y-3 sm:space-y-4">
            {comments.map((comment) => (
              <div key={comment.id} className="border border-gray-200 rounded-lg p-3 sm:p-4">
                <div className="flex items-center gap-2 mb-2">
                  {renderStars(comment.rating)}
                </div>
                <p className="text-gray-700 text-sm sm:text-base">{comment.comment}</p>
                <p className="text-xs text-gray-500 mt-2">
                  by {comment.userName} • {comment.timestamp?.toDate().toLocaleDateString()}
                </p>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500 text-sm sm:text-base">No reviews yet.</p>
        )}
      </div>

      {/* ✅ 채팅 모달 */}
      {showChatModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
          <div className="bg-white rounded-xl w-full max-w-2xl h-[80vh] sm:h-[70vh] flex flex-col">
            <div className="p-3 sm:p-4 border-b border-gray-200 flex justify-between items-center">
              <div>
                <h3 className="font-semibold text-sm sm:text-base">Chat with {product.sellerName}</h3>
                <p className="text-xs sm:text-sm text-gray-500">Business: {sellerProfile?.company || 'N/A'}</p>
              </div>
              <button
                onClick={() => setShowChatModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={20} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-3 sm:p-4 bg-gray-50 space-y-3">
              {loadingChat ? (
                <div className="text-center py-4 text-sm">Loading messages...</div>
              ) : chatMessages.length === 0 ? (
                <p className="text-gray-500 text-center py-4 text-sm">No messages yet. Start the conversation!</p>
              ) : (
                chatMessages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`max-w-xs p-2 sm:p-3 rounded-lg ${
                      msg.senderId === user.uid
                        ? 'bg-blue-500 text-white ml-auto'
                        : 'bg-gray-200 text-gray-800'
                    }`}
                  >
                    <p className="text-sm">{msg.text}</p>
                    <p className="text-xs opacity-80 mt-1 text-right">
                      {msg.timestamp?.toDate().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                ))
              )}
              <div ref={messagesEndRef} />
            </div>
            <div className="p-3 sm:p-4 border-t border-gray-200 flex gap-2">
              <input
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                placeholder="Type your message..."
                className="flex-1 px-2 sm:px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              />
              <button
                onClick={handleSendMessage}
                disabled={!newMessage.trim()}
                className="bg-blue-600 text-white p-2 rounded-lg hover:bg-blue-700 disabled:bg-gray-300"
              >
                <Send size={16} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetailPage;